/**
 * Putting a plan on the calendar, two ways.
 *
 * "Every Mon/Wed/Fri for 6 weeks" is what most people want and it should be
 * three taps. But weeks aren't interchangeable — one has a Wednesday you can't
 * train, the next you're away — and a recurring rule you can't override per
 * week is one people stop trusting and then stop using. So: a weekly pattern,
 * or an explicit list of dates, and the same endpoint takes either.
 *
 * In pick mode, tapping a date cycles it through the plan's days and then off
 * again. One gesture assigns, changes, and clears, which beats a date picker
 * plus a dropdown on a phone.
 */

import { useMemo, useState } from 'react'

import type { Plan } from '../db'
import { api, ApiError } from '../api/client'

const WEEKDAYS = [
  { index: 0, label: 'Mon' },
  { index: 1, label: 'Tue' },
  { index: 2, label: 'Wed' },
  { index: 3, label: 'Thu' },
  { index: 4, label: 'Fri' },
  { index: 5, label: 'Sat' },
  { index: 6, label: 'Sun' },
]

function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

const mondayIndex = (jsDay: number) => (jsDay + 6) % 7

/** The default pattern: spread across the week, not stacked on consecutive days. */
function defaultWeekdays(dayCount: number): number[] {
  const spread: Record<number, number[]> = {
    1: [0], 2: [0, 3], 3: [0, 2, 4], 4: [0, 1, 3, 4],
    5: [0, 1, 2, 3, 4], 6: [0, 1, 2, 3, 4, 5], 7: [0, 1, 2, 3, 4, 5, 6],
  }
  return (spread[dayCount] ?? [0, 2, 4]).slice(0, dayCount)
}

export function Scheduler({
  plan,
  onScheduled,
}: {
  plan: Plan
  onScheduled: (created: number, removed: number) => void
}) {
  const [mode, setMode] = useState<'weekly' | 'pick'>('weekly')
  const [weekdays, setWeekdays] = useState<number[]>(() => defaultWeekdays(plan.days.length))
  const [weeks, setWeeks] = useState(String(plan.weeks || 4))
  const [picked, setPicked] = useState<Record<string, number>>({})   // date -> plan_day_id
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Four weeks from today, which is as far ahead as anyone picks by hand.
  const upcoming = useMemo(() => {
    const start = new Date()
    start.setHours(12, 0, 0, 0)
    // Back up to the Monday of this week so the grid's columns line up with
    // weekday names instead of starting mid-week.
    start.setDate(start.getDate() - mondayIndex(start.getDay()))
    return Array.from({ length: 28 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }, [])

  const todayIso = isoDate(new Date())

  const cycle = (dateIso: string) => {
    setPicked((prev) => {
      const next = { ...prev }
      const current = prev[dateIso]
      const ids = plan.days.map((d) => d.id)
      if (current === undefined) {
        next[dateIso] = ids[0]
      } else {
        const at = ids.indexOf(current)
        if (at === ids.length - 1) delete next[dateIso]
        else next[dateIso] = ids[at + 1]
      }
      return next
    })
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const body = mode === 'weekly'
        ? { weekdays, weeks: parseInt(weeks, 10) || plan.weeks || 4 }
        : {
            slots: Object.entries(picked).map(([date, plan_day_id]) => ({
              date, plan_day_id,
            })),
          }
      const res = await api<{ created: number; removed?: number }>(
        `/plans/${plan.id}/schedule`, { body })
      // `removed` too, not just `created`: clearing a plan that had nothing on
      // the calendar and clearing one that had six days both came back as
      // created:0, and the screen said "Cleared" either way. When the squares
      // you were looking at belonged to a different plan, that message was
      // simply false.
      onScheduled(res.created, res.removed ?? 0)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Couldn't save the schedule — needs a connection.",
      )
    } finally {
      setBusy(false)
    }
  }

  const pickedCount = Object.keys(picked).length
  // Zero is a valid answer. Selecting nothing and saving takes the plan off
  // the calendar — previously the button just sat disabled, so "I don't want
  // this scheduled any more" was a thing you could think but not say.
  const clearing = mode === 'weekly' ? weekdays.length === 0 : pickedCount === 0
  const dayName = (id: number) => plan.days.find((d) => d.id === id)?.name ?? ''

  return (
    <div>
      <div style={{ display: 'flex', gap: '.4rem', marginBottom: '.5rem' }}>
        <button
          className={mode === 'weekly' ? undefined : 'secondary'}
          style={{ flex: 1, marginTop: 0, fontSize: '.85rem' }}
          onClick={() => setMode('weekly')}
        >
          Every week
        </button>
        <button
          className={mode === 'pick' ? undefined : 'secondary'}
          style={{ flex: 1, marginTop: 0, fontSize: '.85rem' }}
          onClick={() => setMode('pick')}
        >
          Pick dates
        </button>
      </div>

      {mode === 'weekly' ? (
        <>
          <div style={{ display: 'flex', gap: '.25rem', flexWrap: 'wrap' }}>
            {WEEKDAYS.map((d) => {
              const on = weekdays.includes(d.index)
              return (
                <button
                  key={d.index}
                  className={on ? undefined : 'secondary'}
                  style={{ flex: '1 1 2.5rem', marginTop: 0, padding: '.3rem .1rem',
                           minHeight: 0, fontSize: '.8rem' }}
                  onClick={() =>
                    setWeekdays((prev) =>
                      prev.includes(d.index)
                        ? prev.filter((x) => x !== d.index)
                        : [...prev, d.index].sort((a, b) => a - b),
                    )
                  }
                >
                  {d.label}
                </button>
              )
            })}
          </div>

          <label htmlFor="sched-weeks" style={{ marginTop: '.5rem', display: 'block' }}>
            For how many weeks
          </label>
          <input
            id="sched-weeks"
            type="number"
            inputMode="numeric"
            value={weeks}
            onChange={(e) => setWeeks(e.target.value)}
          />

          {weekdays.length > plan.days.length && (
            <p className="muted" style={{ fontSize: '.8rem', margin: '.3rem 0 0' }}>
              You've picked more days than the plan has, so its {plan.days.length}{' '}
              days will repeat to fill the week.
            </p>
          )}
        </>
      ) : (
        <>
          <p className="muted" style={{ fontSize: '.8rem', margin: '0 0 .4rem' }}>
            Tap a date to assign a day, tap again to change it, once more to clear.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {WEEKDAYS.map((d) => (
              <div key={d.index} className="muted"
                   style={{ textAlign: 'center', fontSize: '.68rem' }}>
                {d.label[0]}
              </div>
            ))}
            {upcoming.map((d) => {
              const iso = isoDate(d)
              const assigned = picked[iso]
              const past = iso < todayIso
              return (
                <button
                  key={iso}
                  className="grid-cell"
                  disabled={past}
                  onClick={() => cycle(iso)}
                  style={{
                    marginTop: 0,
                    minHeight: 0,
                    padding: '.25rem 0 .1rem',
                    fontSize: '.75rem',
                    lineHeight: 1.15,
                    // Past days stay visible so the grid keeps its shape, but
                    // they can't be picked — the server refuses backdating and
                    // an enabled button that always errors is worse than none.
                    opacity: past ? 0.35 : 1,
                    background: assigned !== undefined ? 'var(--surface-2)' : 'transparent',
                    border: iso === todayIso
                      ? '1px solid var(--accent)'
                      : '1px solid transparent',
                    borderRadius: 'var(--radius)',
                    color: 'inherit',
                  }}
                >
                  {d.getDate()}
                  <div className="muted" style={{ fontSize: '.6rem', height: '.8em',
                                                  overflow: 'hidden' }}>
                    {assigned !== undefined ? dayName(assigned).slice(0, 6) : ''}
                  </div>
                </button>
              )
            })}
          </div>
          <p className="muted" style={{ fontSize: '.8rem', marginTop: '.4rem' }}>
            {pickedCount === 0 ? 'No dates picked yet.' : `${pickedCount} scheduled.`}
          </p>
        </>
      )}

      {error && <div className="error" style={{ marginTop: '.5rem' }}>{error}</div>}

      {clearing && (
        <p className="muted" style={{ fontSize: '.8rem', margin: '.4rem 0 0' }}>
          Nothing selected — saving now clears this plan's upcoming days.
          Workouts you've already done stay where they are.
        </p>
      )}

      <button style={{ width: '100%' }} disabled={busy} onClick={save}>
        {busy
          ? (clearing ? 'Clearing…' : 'Scheduling…')
          : (clearing ? 'Clear the schedule' : 'Put it on the calendar')}
      </button>
    </div>
  )
}
